import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Bell, Plus, Check, Clock, AlertTriangle, Calendar, Syringe, 
  Shield, Bug, Pill, Stethoscope, Scissors, Trash2, Edit, ChevronRight
} from "lucide-react";

const REMINDER_ICONS = {
  frontline: Shield,
  antiparassitario: Bug,
  vermifugo: Pill,
  visita_controllo: Stethoscope,
  vaccino: Syringe,
  toelettatura: Scissors,
  altro: Bell
};

const REMINDER_COLORS = {
  frontline: "bg-blue-100 text-blue-600",
  antiparassitario: "bg-green-100 text-green-600",
  vermifugo: "bg-purple-100 text-purple-600",
  visita_controllo: "bg-amber-100 text-amber-600",
  vaccino: "bg-red-100 text-red-600",
  toelettatura: "bg-pink-100 text-pink-600",
  altro: "bg-gray-100 text-gray-600"
};

const FREQUENCY_OPTIONS = [
  { value: 0, label: "Una volta" },
  { value: 7, label: "Settimanale" },
  { value: 14, label: "Ogni 2 settimane" },
  { value: 30, label: "Mensile" },
  { value: 60, label: "Ogni 2 mesi" },
  { value: 90, label: "Trimestrale" },
  { value: 180, label: "Semestrale" },
  { value: 365, label: "Annuale" }
];

export default function Reminders({ petId, petName, compact = false }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [reminderTypes, setReminderTypes] = useState({});
  
  const [newReminder, setNewReminder] = useState({
    pet_id: petId,
    reminder_type: "frontline",
    title: "",
    notes: "",
    frequency_days: 30,
    next_date: "",
    notify_days_before: 1
  });

  useEffect(() => {
    if (petId) {
      fetchReminders();
      fetchReminderTypes();
    }
  }, [petId]);

  const fetchReminders = async () => {
    try {
      const res = await authFetch(`${API}/reminders?pet_id=${petId}`);
      if (res.ok) {
        const data = await res.json();
        setReminders(data);
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminderTypes = async () => {
    try {
      const res = await authFetch(`${API}/reminders/types`);
      if (res.ok) {
        const data = await res.json();
        setReminderTypes(data);
      }
    } catch (error) {
      console.error("Error fetching reminder types:", error);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.next_date) {
      toast.error("Seleziona una data");
      return;
    }

    try {
      const res = await authFetch(`${API}/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newReminder)
      });

      if (res.ok) {
        toast.success("Promemoria creato!");
        setShowAddDialog(false);
        fetchReminders();
        // Reset form
        setNewReminder({
          pet_id: petId,
          reminder_type: "frontline",
          title: "",
          notes: "",
          frequency_days: 30,
          next_date: "",
          notify_days_before: 1
        });
      } else {
        const data = await res.json();
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      const res = await authFetch(`${API}/reminders/${reminderId}/complete`, {
        method: 'POST'
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchReminders();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDelete = async (reminderId) => {
    if (!confirm("Eliminare questo promemoria?")) return;

    try {
      const res = await authFetch(`${API}/reminders/${reminderId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success("Promemoria eliminato");
        fetchReminders();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-50 border-red-200';
      case 'today':
        return 'bg-amber-50 border-amber-200';
      case 'upcoming':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-white border-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#1D1D1F]">Promemoria</h3>
        <Button 
          size="sm"
          onClick={() => setShowAddDialog(true)}
          className="bg-[#FF6B4A] hover:bg-[#E55A3C] rounded-xl h-9"
          data-testid="add-reminder-btn"
        >
          <Plus size={16} className="mr-1" /> Nuovo
        </Button>
      </div>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="text-center py-8 bg-[#F5F5F7] rounded-2xl">
          <Bell className="mx-auto text-[#86868B] mb-2" size={32} />
          <p className="text-[#86868B]">Nessun promemoria</p>
          <p className="text-xs text-[#86868B] mt-1">Aggiungi promemoria per Frontline, antiparassitari e altro</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reminders.map(reminder => {
            const Icon = REMINDER_ICONS[reminder.reminder_type] || Bell;
            const colorClass = REMINDER_COLORS[reminder.reminder_type] || REMINDER_COLORS.altro;
            
            return (
              <div 
                key={reminder.reminder_id}
                className={`p-4 rounded-2xl border-2 transition-all ${getStatusStyle(reminder.status)}`}
                data-testid={`reminder-${reminder.reminder_id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon size={20} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-[#1D1D1F]">{reminder.title}</h4>
                      
                      {/* Status Badge */}
                      {reminder.status === 'overdue' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full flex items-center gap-1">
                          <AlertTriangle size={10} /> Scaduto
                        </span>
                      )}
                      {reminder.status === 'today' && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                          Oggi
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-[#86868B] mt-0.5">
                      {reminder.status_label}
                      {reminder.frequency_days > 0 && (
                        <span className="ml-2 text-xs">
                          (ogni {reminder.frequency_days} giorni)
                        </span>
                      )}
                    </p>
                    
                    {reminder.notes && (
                      <p className="text-xs text-[#86868B] mt-1 line-clamp-1">{reminder.notes}</p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleComplete(reminder.reminder_id)}
                      className="h-8 w-8 p-0 rounded-lg hover:bg-green-100"
                      title="Segna come completato"
                    >
                      <Check size={16} className="text-green-600" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(reminder.reminder_id)}
                      className="h-8 w-8 p-0 rounded-lg hover:bg-red-100"
                      title="Elimina"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Reminder Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md rounded-3xl border-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D1D1F]">
              Nuovo Promemoria
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Tipo *</Label>
              <Select 
                value={newReminder.reminder_type} 
                onValueChange={(v) => {
                  setNewReminder({
                    ...newReminder, 
                    reminder_type: v,
                    title: reminderTypes[v]?.label || ""
                  });
                }}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="reminder-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(reminderTypes).map(([key, val]) => {
                    const Icon = REMINDER_ICONS[key] || Bell;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon size={16} className="text-[#86868B]" />
                          <span>{val.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Titolo</Label>
              <Input
                value={newReminder.title}
                onChange={(e) => setNewReminder({...newReminder, title: e.target.value})}
                placeholder={reminderTypes[newReminder.reminder_type]?.label || "Promemoria"}
                className="h-12 rounded-xl"
              />
            </div>

            {/* Next Date */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Prossima data *</Label>
              <Input
                type="date"
                value={newReminder.next_date}
                onChange={(e) => setNewReminder({...newReminder, next_date: e.target.value})}
                className="h-12 rounded-xl"
                data-testid="reminder-date"
              />
            </div>

            {/* Frequency */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Ripeti</Label>
              <Select 
                value={newReminder.frequency_days.toString()} 
                onValueChange={(v) => setNewReminder({...newReminder, frequency_days: parseInt(v)})}
              >
                <SelectTrigger className="h-12 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Note</Label>
              <Textarea
                value={newReminder.notes}
                onChange={(e) => setNewReminder({...newReminder, notes: e.target.value})}
                placeholder="Note aggiuntive..."
                className="rounded-xl resize-none"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAddDialog(false)}
              className="rounded-xl"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleCreateReminder}
              className="bg-[#FF6B4A] hover:bg-[#E55A3C] rounded-xl"
              data-testid="save-reminder-btn"
            >
              Crea promemoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
